import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { canView, canEdit, getUserType } from "../permissions";
import API_BASE from "../config";

const Units = () => {
    const navigate = useNavigate();
    const userType = getUserType();
    const allowView = canView(userType, "units");
    const allowEdit = canEdit(userType, "units");

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [projectFilter, setProjectFilter] = useState("");
    const [page, setPage] = useState(1);
    const perPage = 12;

    useEffect(() => {
        if (!allowView) {
            navigate('/dashboard');
            return;
        }
        setLoading(true);
        fetch(`${API_BASE}/api/allrows`, {
            method: "POST",
            headers: { Authorization: "1", "Content-Type": "application/json" },
            body: JSON.stringify({ token: "3232", table: "units" }),
        })
            .then((r) => r.json())
            .then((json) => {
                setLoading(false);
                if (Array.isArray(json.data)) setData(json.data);
                else setError("Failed to load units.");
            })
            .catch(() => {
                setLoading(false);
                setError("Error fetching units.");
            });
    }, [allowView, navigate]);

    if (!allowView) return null;

    // Filters
    let filtered = data.filter((i) =>
        Object.values(i).join(" ").toLowerCase().includes(search.toLowerCase())
    );
    if (statusFilter) filtered = filtered.filter((i) => i.status === statusFilter);
    if (projectFilter) filtered = filtered.filter((i) => (i.project || "") === projectFilter);

    const totalPages = Math.ceil(filtered.length / perPage);
    const rows = filtered.slice((page - 1) * perPage, page * perPage);

    // Dynamic filter options
    const statuses = [...new Set(data.map((d) => d.status).filter(Boolean))];
    const projects = [...new Set(data.map((d) => d.project).filter(Boolean))];

    const fmt = (v) => {
        const n = Number(v);
        return isNaN(n) || n === 0 ? "—" : "₹" + n.toLocaleString("en-IN");
    };

    const getStatusClass = (status) => {
        if (!status) return "un-badge-na";
        const s = status.toLowerCase();
        if (s === "available" || s === "open") return "un-badge-available";
        if (s === "sold" || s === "booked") return "un-badge-sold";
        if (s === "reserved" || s === "hold") return "un-badge-reserved";
        if (s === "registry") return "un-badge-registry";
        return "un-badge-na";
    };

    return (
        <>
            <style>{`
        .un-page { display:flex; flex-direction:column; gap:20px; }
        .un-header { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
        .un-title-wrap { display:flex; align-items:center; gap:12px; }
        .un-title { font-size:22px; font-weight:700; color:#0f172a; }
        .un-count { font-size:13px; color:#64748b; margin-top:2px; }
        .un-stats { display:flex; gap:14px; flex-wrap:wrap; }
        .un-stat-card { background:#fff; border:1px solid #e2e8f0; border-radius:12px; padding:14px 20px; min-width:120px; box-shadow:0 1px 3px rgba(0,0,0,.04); }
        .un-stat-val { font-size:22px; font-weight:800; color:#0f172a; }
        .un-stat-lbl { font-size:11px; color:#64748b; text-transform:uppercase; font-weight:600; letter-spacing:.4px; margin-top:2px; }
        .un-stat-card.available .un-stat-val { color:#059669; }
        .un-stat-card.sold .un-stat-val { color:#dc2626; }
        .un-stat-card.reserved .un-stat-val { color:#d97706; }
        .un-stat-card.registry { border-color:#c7d2fe; background:linear-gradient(135deg,#fafafe,#eef2ff); }
        .un-stat-card.registry .un-stat-val { color:#6366f1; }
        .un-stat-card.registry .un-stat-lbl { color:#6366f1; }

        .un-filters { background:#fff; padding:16px; border-radius:14px; border:1px solid #e2e8f0; box-shadow:0 1px 3px rgba(0,0,0,.04); display:flex; gap:12px; flex-wrap:wrap; align-items:center; }
        .un-filters input,.un-filters select { padding:10px 14px; border-radius:10px; border:1px solid #e2e8f0; outline:none; min-width:150px; font-size:13px; font-family:'Inter',sans-serif; color:#334155; background:#f8fafc; transition:all .2s; }
        .un-filters input { flex:1; min-width:200px; }
        .un-filters input:focus,.un-filters select:focus { border-color:#818cf8; box-shadow:0 0 0 3px rgba(99,102,241,.1); background:#fff; }

        .un-wrap { background:#fff; border-radius:14px; border:1px solid #e2e8f0; box-shadow:0 1px 3px rgba(0,0,0,.04); overflow-x:auto; }
        .un-table { width:100%; border-collapse:collapse; min-width:900px; }
        .un-table th,.un-table td { padding:13px 14px; font-size:13px; text-align:left; border-bottom:1px solid #f1f5f9; }
        .un-table th { background:#f8fafc; color:#64748b; font-weight:600; font-size:11px; text-transform:uppercase; letter-spacing:.5px; white-space:nowrap; position:sticky; top:0; }
        .un-table tbody tr { transition:background .15s; }
        .un-table tbody tr:hover { background:#f8fafc; }
        .un-table td { color:#334155; font-weight:500; }

        .un-unit-name { font-weight:700; color:#0f172a; }
        .un-unit-sub { font-size:11px; color:#94a3b8; margin-top:1px; }
        .un-price { font-weight:700; color:#0f172a; font-variant-numeric:tabular-nums; }
        .un-area { font-weight:600; color:#475569; }
        .un-project { font-weight:600; color:#6366f1; }
        .un-floor { font-weight:500; color:#64748b; }
        .un-type { display:inline-block; padding:3px 10px; border-radius:6px; font-size:11px; font-weight:600; background:#f0f9ff; color:#0369a1; border:1px solid #bae6fd; }

        .un-badge { display:inline-block; padding:4px 12px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; }
        .un-badge-available { background:#ecfdf5; color:#059669; border:1px solid #a7f3d0; }
        .un-badge-sold { background:#fef2f2; color:#dc2626; border:1px solid #fecaca; }
        .un-badge-reserved { background:#fffbeb; color:#d97706; border:1px solid #fcd34d; }
        .un-badge-registry { background:#eef2ff; color:#6366f1; border:1px solid #c7d2fe; }
        .un-badge-na { background:#f8fafc; color:#94a3b8; border:1px solid #e2e8f0; }

        .un-edit { padding:7px 16px; border:1px solid #e2e8f0; border-radius:8px; background:#f8fafc; color:#6366f1; cursor:pointer; font-size:12px; font-weight:600; font-family:'Inter',sans-serif; transition:all .2s; white-space:nowrap; display:inline-flex; align-items:center; gap:5px; }
        .un-edit:hover { background:#eef2ff; border-color:#a5b4fc; box-shadow:0 2px 8px rgba(99,102,241,.12); transform:translateY(-1px); }

        .un-pag { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
        .un-pag-info { font-size:13px; color:#64748b; }
        .un-pag-btns { display:flex; align-items:center; gap:6px; }
        .un-pag-btns button { padding:8px 14px; border-radius:8px; border:1px solid #e2e8f0; background:#fff; font-size:13px; font-weight:500; color:#334155; cursor:pointer; transition:all .2s; font-family:'Inter',sans-serif; }
        .un-pag-btns button:hover:not(:disabled) { background:#f1f5f9; }
        .un-pag-btns button:disabled { opacity:.4; cursor:not-allowed; }
        .un-pag-btns span { font-size:13px; color:#64748b; padding:0 6px; }

        .un-load { display:flex; flex-direction:column; align-items:center; padding:60px 20px; gap:14px; }
        .un-spin { width:36px; height:36px; border:3px solid #e0e7ff; border-top-color:#6366f1; border-radius:50%; animation:un-sp .7s linear infinite; }
        @keyframes un-sp { to{transform:rotate(360deg)} }
        .un-load-t { font-size:14px; color:#64748b; }
        .un-err { padding:16px; background:#fef2f2; border:1px solid #fecaca; border-radius:12px; color:#dc2626; font-size:14px; text-align:center; }
        .un-empty { padding:40px 20px; text-align:center; color:#94a3b8; font-size:14px; }
        .un-empty-icon { font-size:40px; margin-bottom:10px; }

        @media(max-width:640px){
          .un-filters{flex-direction:column;align-items:stretch;}
          .un-filters input,.un-filters select{min-width:100%;}
          .un-pag{flex-direction:column;align-items:stretch;text-align:center;}
          .un-pag-btns{justify-content:center;}
          .un-stats{flex-direction:column;}
        }
      `}</style>

            <div className="un-page">
                {/* Header */}
                <div className="un-header">
                    <div>
                        <div className="un-title-wrap">
                            <h2 className="un-title">🏢 Units</h2>
                        </div>
                        <div className="un-count">
                            {loading ? "Loading..." : `${filtered.length} total unit${filtered.length !== 1 ? "s" : ""}`}
                        </div>
                    </div>
                </div>

                {/* Summary Stats */}
                {!loading && data.length > 0 && (
                    <div className="un-stats">
                        <div className="un-stat-card">
                            <div className="un-stat-val">{data.length}</div>
                            <div className="un-stat-lbl">Total Units</div>
                        </div>
                        <div className="un-stat-card available">
                            <div className="un-stat-val">
                                {data.filter((d) => (d.status || "").toLowerCase() === "available" || (d.status || "").toLowerCase() === "open").length}
                            </div>
                            <div className="un-stat-lbl">Available</div>
                        </div>
                        <div className="un-stat-card sold">
                            <div className="un-stat-val">
                                {data.filter((d) => (d.status || "").toLowerCase() === "sold" || (d.status || "").toLowerCase() === "booked").length}
                            </div>
                            <div className="un-stat-lbl">Sold / Booked</div>
                        </div>

                        <div className="un-stat-card registry">
                            <div className="un-stat-val">
                                {data.filter((d) => (d.status || "").toLowerCase() === "registry").length}
                            </div>
                            <div className="un-stat-lbl">📝 Registry</div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="un-filters">
                    <input
                        placeholder="🔍 Search by unit name, project, type..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    />
                    <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                        <option value="">All Status</option>
                        {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {projects.length > 0 && (
                        <select value={projectFilter} onChange={(e) => { setProjectFilter(e.target.value); setPage(1); }}>
                            <option value="">All Projects</option>
                            {projects.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                    )}
                </div>

                {error && <div className="un-err">⚠️ {error}</div>}

                {loading ? (
                    <div className="un-load">
                        <div className="un-spin" />
                        <div className="un-load-t">Loading units...</div>
                    </div>
                ) : (
                    <>
                        <div className="un-wrap">
                            <table className="un-table">
                                <thead>
                                    <tr>
                                        <th>Unit</th>
                                        <th>Project</th>
                                        <th>Type</th>
                                        <th>Sale Type</th>
                                        <th>Plot Area</th>
                                        <th>Unit Size</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan="8">
                                                <div className="un-empty">
                                                    <div className="un-empty-icon">🏢</div>
                                                    No units found
                                                </div>
                                            </td>
                                        </tr>
                                    ) : rows.map((u) => (
                                        <tr key={u.id}>
                                            <td>
                                                <div className="un-unit-name">{u.unit_name || u.name || u.unit || `Unit #${u.id}`}</div>
                                                <div className="un-unit-sub">ID: {u.id}</div>
                                            </td>
                                            <td><span className="un-project">{u.project || "—"}</span></td>
                                            <td>
                                                {u.unit_type
                                                    ? <span className="un-type">{u.unit_type}</span>
                                                    : "—"
                                                }
                                            </td>
                                            <td><span className="un-floor">{u.sale_type || "—"}</span></td>
                                            <td><span className="un-price">{u.plot_area || "—"}</span></td>
                                            <td><span className="un-area">{u.unit_size || "—"}</span></td>
                                            <td>
                                                <span className={`un-badge ${getStatusClass(u.status)}`}>
                                                    {u.status || "—"}
                                                </span>
                                            </td>
                                            <td>
                                                {allowEdit ? (
                                                    <button
                                                        className="un-edit"
                                                        onClick={() => navigate("/dashboard/units/edit", { state: { unit: u } })}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                ) : (
                                                    <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>View Only</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="un-pag">
                                <div className="un-pag-info">
                                    Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}
                                </div>
                                <div className="un-pag-btns">
                                    <button disabled={page === 1} onClick={() => setPage(1)}>First</button>
                                    <button disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                                    <span>Page {page} of {totalPages}</span>
                                    <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                                    <button disabled={page === totalPages} onClick={() => setPage(totalPages)}>Last</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default Units;
